import { averageValues, extentFromValues } from "../core/math.js";
import { canonicalValueForField, createParserWarning, CANONICAL_OPTIONAL_BOOLEAN_FIELDS, CANONICAL_OPTIONAL_TEXT_FIELDS } from "../core/schema.js";
import { finiteValues, quantile } from "../analysis/utils.js";
import { assignFlightPhases } from "../analysis/phase-detection.js";
import {
  CANONICAL_SENSOR_NUMBER_FIELDS,
  aggregateNumericSamples,
  aggregateBooleanSamples,
  nearestBucketValue,
  hasCanonicalSensorData,
} from "./aggregation-policy.js";
export { hasCanonicalSensorData } from "./aggregation-policy.js";

// Visibility for silent data-loss: surface when the engine-sensor filter
// in finalizeParsedPoints discards more than half of a non-trivial row
// count. The most common cause is header drift in a CSV the parser
// didn't notice — e.g. all sensor columns renamed — which leaves the
// caller with a near-empty flight that ingest still marks "completed".
export function warnOnExcessiveRowDrop(inputCount, keptCount, label) {
  if (inputCount < 60) return;
  if (keptCount >= inputCount * 0.5) return;
  const dropped = inputCount - keptCount;
  const pct = Math.round((dropped / inputCount) * 100);
  console.warn(
    `${label}: dropped ${dropped} of ${inputCount} rows (${pct}%) — `
    + "no recognizable engine sensor fields. Check for header drift or unknown CSV format.",
  );
}

export function timeOfDayToSeconds(value) {
  const match = String(value ?? "").trim().match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if (hours >= 24 || minutes >= 60 || seconds >= 60) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

export function elapsedSecondsFromClockRows(rows, key) {
  const elapsed = [];
  let firstAbsolute = null;
  let previousAbsolute = null;
  let dayOffset = 0;

  rows.forEach((row) => {
    const clockSeconds = timeOfDayToSeconds(row[key]);
    if (!Number.isFinite(clockSeconds)) {
      elapsed.push(null);
      return;
    }

    if (Number.isFinite(previousAbsolute) && clockSeconds + dayOffset < previousAbsolute - 43200) {
      dayOffset += 86400;
    }
    const absoluteSeconds = clockSeconds + dayOffset;
    if (!Number.isFinite(firstAbsolute)) firstAbsolute = absoluteSeconds;
    elapsed.push(absoluteSeconds - firstAbsolute);
    previousAbsolute = absoluteSeconds;
  });

  return elapsed;
}

export function elapsedDeltaBetween(points, fromIndex, toIndex, fallback = 1) {
  const fromTime = points[fromIndex]?.elapsedSeconds ?? fromIndex;
  const toTime = points[toIndex]?.elapsedSeconds ?? toIndex;
  const delta = toTime - fromTime;
  return Number.isFinite(delta) && delta > 0 ? delta : fallback;
}

export function hasMultipleSamplesPerSecond(points) {
  const firstElapsed = points.find((point) => Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? 0;
  const buckets = new Map();
  for (const point of points) {
    if (!Number.isFinite(point.elapsedSeconds)) continue;
    const bucket = Math.floor(point.elapsedSeconds - firstElapsed + 1e-6);
    const count = (buckets.get(bucket) ?? 0) + 1;
    if (count > 1) return true;
    buckets.set(bucket, count);
  }
  return false;
}

export function sampleIntervalSeconds(points) {
  const diffs = [];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1].elapsedSeconds;
    const current = points[index].elapsedSeconds;
    if (Number.isFinite(previous) && Number.isFinite(current) && current > previous) {
      diffs.push(current - previous);
    }
  }
  return quantile(diffs, 0.5) ?? 1;
}

const GPS_FIX_EPSILON = 1e-9;
const GPS_AWARE_BUCKET_KEYS = new Set([
  "groundSpeed",
  "altitude",
  "latitude",
  "longitude",
  "gpsTrack",
  "gpsAltitude",
]);

function pointHasUsableGpsFix(point) {
  return Number.isFinite(point?.latitude)
    && Number.isFinite(point?.longitude)
    && Math.abs(point.latitude) > GPS_FIX_EPSILON
    && Math.abs(point.longitude) > GPS_FIX_EPSILON;
}

function averageBucketNumericValue(bucketPoints, key) {
  const gpsScoped = GPS_AWARE_BUCKET_KEYS.has(key) && bucketPoints.some(pointHasUsableGpsFix);
  const values = bucketPoints
    .filter((point) => !gpsScoped || pointHasUsableGpsFix(point))
    .map((point) => point[key])
    .filter(Number.isFinite);
  return aggregateNumericSamples(values, key);
}

export function suppressAuxDropoutRows(points) {
  return points.map((point, index) => {
    const previous = points[index - 1];
    if (!previous || point.elapsedSeconds < 120) return point;
    const droppedToZero = ["buss", "amps", "oat", "carbTemp", "fuelUsed"].filter((key) => {
      return point[key] === 0 && Number.isFinite(previous[key]) && previous[key] > 0;
    }).length;
    if (droppedToZero < 3) return point;
    return {
      ...point,
      fuelUsed: null,
      buss: null,
      amps: null,
      oat: null,
      carbTemp: null,
      oilTemp: null,
      oilPressure: null,
    };
  });
}

export function suppressImplausibleCarbTemp(points) {
  const values = finiteValues(points, "carbTemp");
  if (values.length < 10) return points;

  const median = quantile(values, 0.5) ?? 0;
  const highShare = values.filter((value) => value > 220).length / values.length;
  const suppressChannel = median > 180 || highShare > 0.25;

  return points.map((point) => {
    if (!Number.isFinite(point.carbTemp)) return point;
    if (suppressChannel || point.carbTemp < -80 || point.carbTemp > 240) {
      return { ...point, carbTemp: null };
    }
    return point;
  });
}

export function suppressImplausibleOilTemps(points) {
  return points.map((point) => {
    let next = point;
    let sideChanged = false;
    [
      ["oilTemp1", "oilTempLeft"],
      ["oilTemp2", "oilTempRight"],
    ].forEach(([key, legacyKey]) => {
      const value = canonicalValueForField(point, key);
      if (Number.isFinite(value) && value < 0) {
        if (next === point) next = { ...point };
        if (next[key] !== undefined) next[key] = null;
        if (next[legacyKey] !== undefined) next[legacyKey] = null;
        sideChanged = true;
      }
    });
    if (sideChanged) {
      const average = averageValues([canonicalValueForField(next, "oilTemp1"), canonicalValueForField(next, "oilTemp2")]);
      next.oilTemp = Number.isFinite(average) ? average : null;
      return next;
    }
    if (Number.isFinite(point.oilTemp) && point.oilTemp < 0) {
      return { ...point, oilTemp: null };
    }
    return next;
  });
}

export function isSingleSampleAltitudeImpulse(points, index) {
  if (index <= 0 || index >= points.length - 1) return false;
  const previous = points[index - 1];
  const point = points[index];
  const next = points[index + 1];
  const previousAltitude = previous?.altitude;
  const altitude = point?.altitude;
  const nextAltitude = next?.altitude;
  if (!Number.isFinite(previousAltitude) || !Number.isFinite(altitude) || !Number.isFinite(nextAltitude)) return false;

  const previousDelta = altitude - previousAltitude;
  const nextDelta = nextAltitude - altitude;
  if (Math.sign(previousDelta) === Math.sign(nextDelta)) return false;

  const seconds = elapsedDeltaBetween(points, index - 1, index + 1, 2);
  const neighborDelta = Math.abs(nextAltitude - previousAltitude);
  const impulseDelta = Math.min(Math.abs(previousDelta), Math.abs(nextDelta));
  const neighborsClose = neighborDelta <= Math.max(250, seconds * 80);
  if (!neighborsClose || impulseDelta < Math.max(500, seconds * 120)) return false;

  const neighborAverage = (previousAltitude + nextAltitude) / 2;
  const airborneDropout = neighborAverage > 250 && altitude < 150;
  const groundSpike = neighborAverage < 500 && altitude > 1200;
  return airborneDropout || groundSpike;
}

export function suppressImplausibleAltitude(points) {
  if (finiteValues(points, "altitude").length < 3) return points;
  return points.map((point, index) => {
    if (!isSingleSampleAltitudeImpulse(points, index)) return point;
    const previousAltitude = points[index - 1].altitude;
    const nextAltitude = points[index + 1].altitude;
    return {
      ...point,
      altitude: (previousAltitude + nextAltitude) / 2,
      verticalSpeed: null,
    };
  });
}

function channelFiniteCount(points, key) {
  return finiteValues(points, key).length;
}

function channelAbsMax(points, key) {
  const values = finiteValues(points, key).map((value) => Math.abs(value));
  return values.length ? Math.max(...values) : null;
}

function channelRange(points, key) {
  const values = finiteValues(points, key);
  if (!values.length) return null;
  return Math.max(...values) - Math.min(...values);
}

function channelAbsentOrNearZero(points, key, tolerance) {
  const count = channelFiniteCount(points, key);
  if (!count) return true;
  const absMax = channelAbsMax(points, key);
  return Number.isFinite(absMax) && absMax <= tolerance;
}

export function suppressPlaceholderGpsMotion(points) {
  if (!points.length) return points;
  const speedCount = channelFiniteCount(points, "groundSpeed");
  const speedAbsMax = channelAbsMax(points, "groundSpeed");
  const speedLooksZeroed = speedCount >= points.length * 0.35
    && Number.isFinite(speedAbsMax)
    && speedAbsMax <= 1;
  if (!speedLooksZeroed) return points;

  const gpsAltitudeAbsentOrZero = channelAbsentOrNearZero(points, "gpsAltitude", 50);
  const altitudeAbsentOrZero = channelAbsentOrNearZero(points, "altitude", 50);
  const latitudeAbsentOrZero = channelAbsentOrNearZero(points, "latitude", 0.01);
  const longitudeAbsentOrZero = channelAbsentOrNearZero(points, "longitude", 0.01);
  const gpsTrackAbsentOrZero = channelAbsentOrNearZero(points, "gpsTrack", 1);
  const zeroedGpsBlock = gpsAltitudeAbsentOrZero
    && altitudeAbsentOrZero
    && latitudeAbsentOrZero
    && longitudeAbsentOrZero
    && gpsTrackAbsentOrZero;
  if (!zeroedGpsBlock) return points;

  const altitudeRange = channelRange(points, "altitude");
  const gpsAltitudeRange = channelRange(points, "gpsAltitude");
  return points.map((point) => ({
    ...point,
    groundSpeed: null,
    altitude: Number.isFinite(altitudeRange) && altitudeRange <= 50 ? null : point.altitude,
    gpsAltitude: Number.isFinite(gpsAltitudeRange) && gpsAltitudeRange <= 50 ? null : point.gpsAltitude,
    latitude: null,
    longitude: null,
    gpsTrack: null,
  }));
}

const GPS_HELD_POSITION_MIN_GROUND_SPEED_KT = 60;
const GPS_MAX_PLAUSIBLE_SPEED_KT = 480;
// Below this the reported ground speed no longer bounds a displacement (a
// taxiing aircraft still cannot cover miles); a zero or missing speed channel
// falls back to the fixed ceiling instead, so a dead speed feed cannot erase
// a good track.
const GPS_MIN_SPEED_ALLOWANCE_KT = 40;
const GPS_REFRESH_BURST_MIN_HOLD_SECONDS = 5;
const GPS_REFRESH_COMPLETION_WINDOW_SECONDS = 60;
// A held corrupt value carried across refreshes of the other axis forms a
// short run of mutually consistent wrong fixes; a genuine detour lasts longer.
const GPS_MAX_EXCURSION_SECONDS = 300;
const GPS_SPEED_ALLOWANCE_FACTOR = 1.5;
const GPS_SPARSE_UPDATES_PER_MOVING_MINUTE = 6;
const GPS_SPARSE_MIN_MOVING_SECONDS = 120;
const EARTH_RADIUS_NM = 3440.065;

function greatCircleNm(a, b) {
  const toRad = Math.PI / 180;
  const dLat = (b.latitude - a.latitude) * toRad;
  const dLon = (b.longitude - a.longitude) * toRad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.latitude * toRad) * Math.cos(b.latitude * toRad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(h)));
}

type GpsFixGroup = { first: number; indices: number[]; elapsedSeconds: number };

function samePosition(a, b) {
  return a.latitude === b.latitude && a.longitude === b.longitude;
}

// Consecutive samples at the same position (three rows in one second, or a
// held value that survived the hold filter) are one fix: judged together and
// blanked together, so duplicates cannot vouch for a corrupt value.
function gpsFixGroups(points): GpsFixGroup[] {
  const groups: GpsFixGroup[] = [];
  points.forEach((point, index) => {
    if (!pointHasUsableGpsFix(point) || !Number.isFinite(point.elapsedSeconds)) return;
    const last = groups.at(-1);
    if (last && samePosition(points[last.first], point)) {
      last.indices.push(index);
      return;
    }
    groups.push({ first: index, indices: [index], elapsedSeconds: point.elapsedSeconds });
  });
  return groups;
}

function blankGroups(points, groups) {
  if (!groups.length) return points;
  const blanked = new Set(groups.flatMap((group) => group.indices));
  return points.map((point, index) => (blanked.has(index) ? { ...point, latitude: null, longitude: null } : point));
}

// The CGR refreshes latitude and longitude in separate seconds, up to half a
// minute apart. After a hold, a change to one axis alone is a partial refresh
// when the other axis changes within the completion window: the partial
// position (an L-shaped corner on the map) is blanked and the completed one
// kept. A dense track is safe: it never holds long enough while moving, and
// motion along one axis (a runway on a meridian) changes that same axis again
// before the other, which ends the search and keeps every sample.
export function collapseGpsRefreshBursts(points, {
  minHoldSeconds = GPS_REFRESH_BURST_MIN_HOLD_SECONDS,
  completionWindowSeconds = GPS_REFRESH_COMPLETION_WINDOW_SECONDS,
} = {}) {
  const groups = gpsFixGroups(points);
  const partial = [];
  const changedAxes = (from, to) => ({
    latitude: points[from.first].latitude !== points[to.first].latitude,
    longitude: points[from.first].longitude !== points[to.first].longitude,
  });
  let index = 1;
  while (index < groups.length) {
    const group = groups[index];
    const previous = groups[index - 1];
    if (group.elapsedSeconds - previous.elapsedSeconds < minHoldSeconds) {
      index += 1;
      continue;
    }
    const first = changedAxes(previous, group);
    if (first.latitude === first.longitude) {
      index += 1;
      continue;
    }
    const waitingFor = first.latitude ? "longitude" : "latitude";
    let completed = null;
    for (let next = index + 1; next < groups.length; next += 1) {
      if (groups[next].elapsedSeconds - group.elapsedSeconds > completionWindowSeconds) break;
      const change = changedAxes(groups[next - 1], groups[next]);
      const firstAxis = first.latitude ? "latitude" : "longitude";
      if (change[waitingFor]) {
        // The completing refresh may nudge the first axis too (the unit
        // accepted a newer sentence). Motion along the first axis that keeps
        // its pace is a track, not a refresh: keep the partial as a fix.
        const partialStep = Math.abs(points[group.first][firstAxis] - points[previous.first][firstAxis]);
        const completionStep = Math.abs(points[groups[next].first][firstAxis] - points[groups[next - 1].first][firstAxis]);
        if (!change[firstAxis] || completionStep <= partialStep * 0.5) completed = next;
        break;
      }
      if (change[firstAxis]) break;
    }
    if (completed === null) {
      index += 1;
      continue;
    }
    for (let k = index; k < completed; k += 1) partial.push(groups[k]);
    index = completed + 1;
  }
  return blankGroups(points, partial);
}

// EI CGR-30P logs repeat the last GPS position, sometimes for minutes, while
// the aircraft is clearly moving: the unit only refreshes the field when it
// accepts a new sentence from its GPS feed. A position repeated into a later
// second at cruise speed is a stale value, not a fix. Keep the samples in the
// second where a position first appears and blank the later repeats, so
// downstream consumers see sparse fixes instead of an aircraft parked at a
// vertex. Stationary and taxi holds (at or below minGroundSpeedKt) stay.
export function suppressHeldGpsPositions(points, { minGroundSpeedKt = GPS_HELD_POSITION_MIN_GROUND_SPEED_KT } = {}) {
  let current = null;
  let changed = false;
  const output = points.map((point, index) => {
    if (!pointHasUsableGpsFix(point)) return point;
    const second = Math.floor(Number.isFinite(point.elapsedSeconds) ? point.elapsedSeconds : index);
    if (!current || !samePosition(current, point)) {
      current = { latitude: point.latitude, longitude: point.longitude, firstSecond: second };
      return point;
    }
    if (second <= current.firstSecond || !(point.groundSpeed > minGroundSpeedKt)) return point;
    changed = true;
    return { ...point, latitude: null, longitude: null };
  });
  return changed ? output : points;
}

// A fix that implies an impossible speed from both neighbours, or from one
// neighbour while the neighbours can reach each other, is a corrupt value (a
// dropped minute digit lands 40 nm away), not a detour. The ground speed the
// log reports over each interval bounds what is plausible; with no speed
// channel a fixed ceiling applies. Only the odd value out is blanked.
export function suppressImplausibleGpsJumps(points, { maxGroundSpeedKt = GPS_MAX_PLAUSIBLE_SPEED_KT } = {}) {
  const groups = gpsFixGroups(points);
  if (groups.length < 3) return points;
  const dropped = new Set<GpsFixGroup>();
  const impliedSpeedKt = (from, to) => {
    const distance = greatCircleNm(points[from.first], points[to.first]);
    if (distance < 0.01) return 0;
    const hours = Math.max(to.elapsedSeconds - from.elapsedSeconds, 1) / 3600;
    return distance / hours;
  };
  const allowedSpeedKt = (from, to) => {
    let reported = null;
    for (let index = from.first; index <= to.first; index += 1) {
      const speed = points[index]?.groundSpeed;
      if (Number.isFinite(speed) && (reported === null || speed > reported)) reported = speed;
    }
    if (reported === null || reported <= 0) return maxGroundSpeedKt;
    return Math.min(maxGroundSpeedKt, Math.max(GPS_MIN_SPEED_ALLOWANCE_KT, reported * GPS_SPEED_ALLOWANCE_FACTOR));
  };
  const implausible = (from, to) => impliedSpeedKt(from, to) > allowedSpeedKt(from, to);
  // Neighbours are re-read after every drop so one corrupt fix cannot condemn
  // the good fix beside it; a few passes settle chains.
  const active = groups.slice();
  for (let pass = 0; pass < 3 && active.length >= 3; pass += 1) {
    let changed = false;
    for (let position = 0; position < active.length;) {
      const group = active[position];
      const previous = active[position - 1];
      const next = active[position + 1];
      let outlier = false;
      if (previous && next) {
        // Unreachable from both sides, or unreachable from one side while
        // the neighbours can reach each other directly: the odd one out.
        const fromPrevious = implausible(previous, group);
        const toNext = implausible(group, next);
        outlier = (fromPrevious && toNext) || ((fromPrevious || toNext) && !implausible(previous, next));
      } else if (!previous) {
        const afterNext = active[position + 2];
        outlier = Boolean(afterNext) && implausible(group, next) && implausible(group, afterNext);
      } else {
        const beforePrevious = active[position - 2];
        outlier = Boolean(beforePrevious) && implausible(previous, group) && implausible(beforePrevious, group);
      }
      if (outlier) {
        dropped.add(group);
        active.splice(position, 1);
        changed = true;
      } else {
        position += 1;
      }
    }
    if (!changed) break;
  }
  // A run of mutually consistent fixes that neither the kept fix before it
  // nor the kept fix after it can reach, while those two can reach each
  // other, is a held corrupt value carried across refreshes of the other axis
  // (the longitude stays wrong while the latitude keeps updating). Blank the
  // run as one excursion; the bracketing fixes must themselves be reachable
  // from their outer neighbours so a good run is never blamed for corrupt
  // brackets.
  for (let pass = 0; pass < 2; pass += 1) {
    let changed = false;
    let start = 0;
    while (start < active.length) {
      let end = start;
      while (end + 1 < active.length && !implausible(active[end], active[end + 1])) end += 1;
      const before = active[start - 1];
      const after = active[end + 1];
      const duration = active[end].elapsedSeconds - active[start].elapsedSeconds;
      const beforeSupported = start < 2 || !implausible(active[start - 2], before);
      const afterSupported = end + 2 >= active.length || !implausible(after, active[end + 2]);
      if (before && after && duration <= GPS_MAX_EXCURSION_SECONDS
        && !implausible(before, after) && beforeSupported && afterSupported) {
        for (let k = start; k <= end; k += 1) dropped.add(active[k]);
        active.splice(start, end - start + 1);
        changed = true;
      } else {
        start = end + 1;
      }
    }
    if (!changed) break;
  }
  return blankGroups(points, [...dropped]);
}

// Informational warning when the position channel updates far less often
// than the log's own cadence while the aircraft is moving (the CGR-30P feed
// above), so the Data Quality panel and the evidence packet can call the
// track approximate. Expects finalized points. A logger that only samples
// every few seconds is not sparse for keeping a fix on every sample.
export function gpsPositionSparsityWarning(points) {
  let movingDurationSeconds = 0;
  let movingSamples = 0;
  let movingUpdates = 0;
  let previousFix = null;
  let previousMovingElapsed = null;
  for (const point of points) {
    const moving = point?.groundSpeed > GPS_HELD_POSITION_MIN_GROUND_SPEED_KT;
    if (moving) {
      movingSamples += 1;
      if (Number.isFinite(point.elapsedSeconds)) {
        if (previousMovingElapsed !== null) {
          movingDurationSeconds += Math.min(Math.max(point.elapsedSeconds - previousMovingElapsed, 0), 60);
        }
        previousMovingElapsed = point.elapsedSeconds;
      }
    } else {
      previousMovingElapsed = null;
    }
    if (!pointHasUsableGpsFix(point)) continue;
    if (moving && (!previousFix || !samePosition(previousFix, point))) movingUpdates += 1;
    previousFix = point;
  }
  if (movingDurationSeconds < GPS_SPARSE_MIN_MOVING_SECONDS || movingUpdates === 0) return null;
  const movingMinutes = movingDurationSeconds / 60;
  const cadencePerMinute = movingSamples / movingMinutes;
  const threshold = Math.min(GPS_SPARSE_UPDATES_PER_MOVING_MINUTE, cadencePerMinute / 4);
  if (movingUpdates / movingMinutes >= threshold) return null;
  const minutes = Math.max(1, Math.round(movingMinutes));
  return createParserWarning({
    code: "gps-sparse-position",
    message: `GPS position updated only ${movingUpdates} times during ${minutes} minutes of movement. The flight track and location-based context are approximate.`,
    severity: "info",
  });
}

export function finalizeParsedPoints(points, { onTransform = null } = {}) {
  const usable = points.filter(hasCanonicalSensorData);
  onTransform?.("filterUnusableSamples", points, usable);
  warnOnExcessiveRowDrop(points.length, usable.length, "finalizeParsedPoints");
  const elapsedExtent = extentFromValues(usable.map((point) => point.elapsedSeconds ?? 0));
  const minElapsed = elapsedExtent?.min ?? 0;
  const maxElapsed = Math.max((elapsedExtent?.max ?? 0) - minElapsed, usable.length - 1, 1);
  const normalized = usable.map((point, index) => ({
    ...point,
    t: (((point.elapsedSeconds ?? index) - minElapsed) / maxElapsed) * 100,
    elapsedSeconds: (point.elapsedSeconds ?? index) - minElapsed,
    ...(!Number.isFinite(point.elapsedSeconds) ? { elapsedTimeEstimated: true } : {}),
  }));
  let cleaned = normalized;
  for (const [name, transform] of [
    ["suppressAuxDropoutRows", suppressAuxDropoutRows],
    ["suppressImplausibleCarbTemp", suppressImplausibleCarbTemp],
    ["suppressImplausibleOilTemps", suppressImplausibleOilTemps],
    ["suppressImplausibleAltitude", suppressImplausibleAltitude],
    ["suppressPlaceholderGpsMotion", suppressPlaceholderGpsMotion],
  ] as const) {
    const output = transform(cleaned);
    onTransform?.(name, cleaned, output);
    cleaned = output;
  }
  const assigned = assignFlightPhases(cleaned);
  const loggedPhases = cleaned.map((point) => String(point.loggedPhase ?? "").trim()).filter(Boolean);
  const hasLoggedPhaseSequence = loggedPhases.length >= cleaned.length * 0.8 && new Set(loggedPhases).size >= 4;
  if (!hasLoggedPhaseSequence) return assigned;
  return assigned.map((point, index) => ({
    ...point,
    phase: String(cleaned[index]?.loggedPhase ?? "").trim() || point.phase,
  }));
}

export function resampleMonotonicPointsToOneHz(points, additionalNumericKeys = []) {
  if (!points.length) return points;
  const firstElapsed = points.find((point) => Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? 0;
  const numericKeys = [...new Set([...CANONICAL_SENSOR_NUMBER_FIELDS, ...additionalNumericKeys])];
  const buckets = new Map();

  points.forEach((point) => {
    if (!Number.isFinite(point.elapsedSeconds)) return;
    const bucket = Math.floor(point.elapsedSeconds - firstElapsed + 1e-6);
    if (!buckets.has(bucket)) buckets.set(bucket, []);
    buckets.get(bucket).push(point);
  });

  return [...buckets.entries()].sort((a, b) => a[0] - b[0]).map(([bucket, bucketPoints]) => {
    const targetElapsed = firstElapsed + bucket + 0.5;
    const representative = bucketPoints.reduce((best, point) => {
      const bestDistance = Math.abs((best.elapsedSeconds ?? targetElapsed) - targetElapsed);
      const pointDistance = Math.abs((point.elapsedSeconds ?? targetElapsed) - targetElapsed);
      return pointDistance < bestDistance ? point : best;
    }, bucketPoints[0]);
    const resampled = {
      ...representative,
      elapsedSeconds: firstElapsed + bucket,
      rawSampleCount: bucketPoints.reduce((total, point) => total + (point.rawSampleCount ?? 1), 0),
      ...(bucketPoints.some((point) => Array.isArray(point.sourceSampleIndices))
        ? { sourceSampleIndices: [...new Set(bucketPoints.flatMap((point) => point.sourceSampleIndices ?? []))] }
        : {}),
    };
    numericKeys.forEach((key) => {
      resampled[key] = averageBucketNumericValue(bucketPoints, key);
    });
    CANONICAL_OPTIONAL_BOOLEAN_FIELDS.forEach((key) => {
      resampled[key] = aggregateBooleanSamples(bucketPoints, key, targetElapsed);
    });
    [...CANONICAL_OPTIONAL_TEXT_FIELDS, "displayTime"].forEach((key) => {
      if (!bucketPoints.some((point) => Object.hasOwn(point, key))) return;
      resampled[key] = nearestBucketValue(bucketPoints, key, targetElapsed,
        (value) => typeof value === "string" && value.trim().length > 0) ?? (key === "displayTime" ? "" : null);
    });
    return resampled;
  });
}

export function resamplePointsToOneHz(points, additionalNumericKeys = []) {
  const interval = sampleIntervalSeconds(points);
  if (!Number.isFinite(interval) || (interval >= 0.75 && !hasMultipleSamplesPerSecond(points))) return points;

  const segments = [];
  let current = [];
  let previousElapsed = null;
  points.forEach((point) => {
    const elapsed = point.elapsedSeconds;
    if (Number.isFinite(elapsed) && Number.isFinite(previousElapsed) && elapsed < previousElapsed - 0.5) {
      if (current.length) segments.push(current);
      current = [];
    }
    current.push(point);
    if (Number.isFinite(elapsed)) previousElapsed = elapsed;
  });
  if (current.length) segments.push(current);

  const output = [];
  let previousOutputPoint = null;
  segments.forEach((segment) => {
    const resampled = resampleMonotonicPointsToOneHz(segment, additionalNumericKeys);
    if (!previousOutputPoint || !resampled.length || resampled[0].elapsedSeconds > previousOutputPoint.elapsedSeconds) {
      output.push(...resampled);
      previousOutputPoint = output.at(-1);
      return;
    }
    const shift = previousOutputPoint.elapsedSeconds + 1 - resampled[0].elapsedSeconds;
    const shifted = resampled.map((point, index) => ({ ...point, elapsedSeconds: point.elapsedSeconds + shift,
      ...(index === 0 ? { elapsedTimeEstimated: true } : {}) }));
    output.push(...shifted);
    previousOutputPoint = output.at(-1);
  });
  return output;
}
